import React, { useEffect, useState } from "react";
import EmojiEventsOutlinedIcon from "@mui/icons-material/EmojiEventsOutlined";
import EnergySavingsLeafOutlinedIcon from "@mui/icons-material/EnergySavingsLeafOutlined";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import LanguageIcon from "@mui/icons-material/Language";
import LightbulbOutlinedIcon from "@mui/icons-material/LightbulbOutlined";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import RocketLaunchOutlinedIcon from "@mui/icons-material/RocketLaunchOutlined";
import ShareIcon from "@mui/icons-material/Share";
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Link from "@mui/material/Link";
import Paper from "@mui/material/Paper";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import { Link as RouterLink } from "react-router-dom";
import { SITE_BG, SURFACE } from "../../dark-theme/palette";
import AuthService from "../../services/AuthService";
import { SUPPORT_FRENCH } from "../../config/supportFrench";
import { getOrderedContentLanguages } from "../../services/LangPrefs";
import Item from "../components/Item";
import { usePagesTranslation } from "../locales";

/** Inactive-state styles aligned with `GridPlayMain` play toolbar IconButtons. */
const playToolbarIconDemoSx = {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 40,
    height: 40,
    flexShrink: 0,
    borderRadius: "50%",
    boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
    backgroundColor: SURFACE.card,
    color: "inherit",
};

const bodySx = { fontSize: "1.1rem", lineHeight: 1.8 };

const toolbarDemoBoxSx = {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    gap: 1,
    p: 1.5,
    borderRadius: 2,
    bgcolor: SURFACE.sunken,
    border: "1px solid",
    borderColor: "divider",
    maxWidth: "fit-content",
};

/** Matches selected-state styling on `SettingsMain` content-language toggles. */
const settingsContentLangToggleSx = {
    "&.Mui-selected": {
        borderColor: "#3BA666 !important",
        borderWidth: "2px !important",
        borderStyle: "solid !important",
        borderLeftColor: "#3BA666 !important",
        borderRightColor: "#3BA666 !important",
        borderTopColor: "#3BA666 !important",
        borderBottomColor: "#3BA666 !important",
        backgroundColor: "rgba(59, 166, 102, 0.08)",
        "&:hover": {
            backgroundColor: "rgba(59, 166, 102, 0.12)",
        },
    },
};

/** SVG icons avoid iOS Safari emoji/font fallback issues (e.g. “?”) in bold headings. */
function TipsSectionHeading({ icon: Icon, children }) {
    return (
        <Box
            sx={{
                display: "flex",
                alignItems: "center",
                gap: 1.25,
                mb: 2,
                flexWrap: "wrap",
            }}
        >
            <Box aria-hidden sx={{ display: "inline-flex", color: "primary.main" }}>
                <Icon sx={{ fontSize: 32 }} />
            </Box>
            <Typography variant="h5" component="h2" sx={{ mb: 0, fontWeight: 600 }}>
                {children}
            </Typography>
        </Box>
    );
}

function LabeledParagraph({ label, body, dense }) {
    return (
        <Typography component="p" sx={{ ...bodySx, mb: dense ? 0 : 2 }}>
            <Box component="span" sx={{ fontWeight: 600 }}>
                {label}
            </Box>{" "}
            {body}
        </Typography>
    );
}

/**
 * How to Play & Pro Tips — narrative guide with play-toolbar icon demos.
 */
export default function HowToTips() {
    const { t } = usePagesTranslation();
    const [demoContentLang, setDemoContentLang] = useState("english");

    /** Cycle preview selection English ↔ French every 5s (read-only demo). */
    useEffect(() => {
        if (!SUPPORT_FRENCH) return undefined;
        const id = window.setInterval(() => {
            setDemoContentLang((prev) => (prev === "english" ? "french" : "english"));
        }, 5000);
        return () => window.clearInterval(id);
    }, []);

    const contentLangDemoOrder = getOrderedContentLanguages(false);

    return (
        <Box sx={{ backgroundColor: SITE_BG, minHeight: "100vh", width: "100%", overflowX: "hidden" }}>
            <Box sx={{ width: "100%", maxWidth: "100%", margin: 0 }}>
                <Item sx={{ p: { xs: 2, md: 4 }, textAlign: "left", width: "100%", maxWidth: "100%", boxSizing: "border-box" }}>
                    <Typography variant="h3" component="h1" gutterBottom sx={{ mb: 2, fontWeight: 600, color: "#1976d2" }}>
                        {t("howto.tips.title")}
                    </Typography>

                    <Typography variant="body1" sx={{ ...bodySx, mb: 4, color: "text.primary" }}>
                        {t("howto.tips.intro")}
                    </Typography>

                    {SUPPORT_FRENCH ? (
                    <>
                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 4 }}>
                        <Box
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: 2,
                                mb: 2,
                                flexWrap: "wrap",
                            }}
                        >
                            <Box
                                aria-hidden
                                sx={{
                                    display: "inline-flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    width: 52,
                                    height: 52,
                                    borderRadius: 2,
                                    flexShrink: 0,
                                    background:
                                        "linear-gradient(145deg, rgba(25, 118, 210, 0.16) 0%, rgba(59, 166, 102, 0.14) 100%)",
                                    boxShadow: "0 2px 10px rgba(25, 118, 210, 0.15)",
                                    border: "1px solid",
                                    borderColor: "rgba(25, 118, 210, 0.2)",
                                }}
                            >
                                <LanguageIcon sx={{ fontSize: 30, color: "primary.main" }} />
                            </Box>
                            <Typography variant="h4" component="h2" sx={{ fontWeight: 600, mb: 0, color: "primary.main" }}>
                                {t("howto.tips.contentLangHeadline")}
                            </Typography>
                        </Box>
                        <Typography component="p" sx={{ ...bodySx, mb: 0 }}>
                            {t("howto.tips.contentLangBodyStart")}
                            <Link component={RouterLink} to="/login" underline="hover" sx={{ fontWeight: 600 }}>
                                {t("howto.tips.contentLangSignIn")}
                            </Link>
                            {t("howto.tips.contentLangBodyOr")}
                            <Link
                                component="button"
                                type="button"
                                onClick={async (e) => {
                                    e.preventDefault();
                                    try {
                                        await AuthService.signup();
                                    } catch {
                                        /* signup redirects to hosted UI */
                                    }
                                }}
                                underline="hover"
                                sx={{
                                    fontWeight: 600,
                                    font: "inherit",
                                    fontSize: "inherit",
                                    lineHeight: "inherit",
                                    fontFamily: "inherit",
                                    color: "primary.main",
                                    p: 0,
                                    border: "none",
                                    background: "none",
                                    cursor: "pointer",
                                    verticalAlign: "baseline",
                                }}
                            >
                                {t("howto.tips.contentLangSignUp")}
                            </Link>
                            {t("howto.tips.contentLangBodyMid")}
                            <Link component={RouterLink} to="/account/settings" underline="hover" sx={{ fontWeight: 600 }}>
                                {t("howto.tips.contentLangSettingsLink")}
                            </Link>
                            {t("howto.tips.contentLangBodyAfter")}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, mt: 2 }}>
                            {t("howto.tips.contentLangCaption")}
                        </Typography>
                        <Box
                            sx={{ pointerEvents: "none", maxWidth: { xs: "100%", sm: 480 } }}
                            aria-hidden
                        >
                            <Paper sx={{ p: 3, width: "100%" }}>
                                <Typography variant="h6" gutterBottom>
                                    {t("profile.settings.content-lang")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                    {t("profile.settings.content-lang-desc")}
                                </Typography>
                                <ToggleButtonGroup
                                    value={demoContentLang}
                                    exclusive
                                    onChange={() => {}}
                                    sx={{ mt: 2 }}
                                >
                                    {contentLangDemoOrder.map((lang) => (
                                        <ToggleButton
                                            key={lang.code}
                                            value={lang.code}
                                            sx={settingsContentLangToggleSx}
                                        >
                                            {lang.name}
                                        </ToggleButton>
                                    ))}
                                </ToggleButtonGroup>
                            </Paper>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />
                    </>
                    ) : null}

                    <Box sx={{ mb: 4 }}>
                        <TipsSectionHeading icon={LightbulbOutlinedIcon}>
                            {t("howto.tips.sectionGoodToKnow")}
                        </TipsSectionHeading>
                        <LabeledParagraph
                            label={t("howto.tips.gtkGridLabel")}
                            body={t("howto.tips.gtkGridBody")}
                        />
                        <LabeledParagraph
                            label={t("howto.tips.gtkExceptionLabel")}
                            body={t("howto.tips.gtkExceptionBody")}
                        />
                        <LabeledParagraph
                            label={t("howto.tips.gtkNoRushLabel")}
                            body={t("howto.tips.gtkNoRushBody")}
                        />
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 4 }}>
                        <TipsSectionHeading icon={EnergySavingsLeafOutlinedIcon}>
                            {t("howto.tips.sectionBeginner")}
                        </TipsSectionHeading>
                        <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 2, fontSize: "1.1rem", lineHeight: 1.8 } }}>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.begStartLabel")}
                                    body={t("howto.tips.begStartBody")}
                                />
                            </li>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.begLearnLabel")}
                                    body={t("howto.tips.begLearnBody")}
                                />
                            </li>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.begRevisedTipLabel")}
                                    body={t("howto.tips.begRevisedTipBody")}
                                />
                            </li>
                            <li>
                                <Typography component="p" sx={{ ...bodySx, mb: 1 }}>
                                    <Box component="span" sx={{ fontWeight: 600 }}>{t("howto.tips.begPdfLabel")}</Box>{" "}
                                    {t("howto.tips.begPdfBody")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {t("howto.tips.begPdfCaption")}
                                </Typography>
                                <Box sx={toolbarDemoBoxSx}>
                                    <Tooltip title={t("puzzle.playGrid.download-pdf")} arrow>
                                        <Box component="span" sx={playToolbarIconDemoSx} aria-hidden>
                                            <PictureAsPdfIcon />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </li>
                            <li>
                                <Typography component="p" sx={{ ...bodySx, mb: 1 }}>
                                    <Box component="span" sx={{ fontWeight: 600 }}>{t("howto.tips.begLeanLabel")}</Box>{" "}
                                    {t("howto.tips.begLeanBody")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {t("howto.tips.begToolbarCaption")}
                                </Typography>
                                <Box sx={toolbarDemoBoxSx}>
                                    <Tooltip title={t("puzzle.playGrid.switch-bold")} arrow>
                                        <Box component="span" sx={playToolbarIconDemoSx} aria-hidden>
                                            <FormatBoldIcon />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </li>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 4 }}>
                        <TipsSectionHeading icon={RocketLaunchOutlinedIcon}>
                            {t("howto.tips.sectionIntermediate")}
                        </TipsSectionHeading>
                        <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 2, fontSize: "1.1rem", lineHeight: 1.8 } }}>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.intFlyLabel")}
                                    body={t("howto.tips.intFlyBody")}
                                />
                            </li>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.intSmartLabel")}
                                    body={t("howto.tips.intSmartBody")}
                                />
                            </li>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.intExpandLabel")}
                                    body={t("howto.tips.intExpandBody")}
                                />
                            </li>
                            <li>
                                <Typography component="p" sx={{ ...bodySx, mb: 1 }}>
                                    <Box component="span" sx={{ fontWeight: 600 }}>{t("howto.tips.intTeamLabel")}</Box>{" "}
                                    {t("howto.tips.intTeamBody")}
                                </Typography>
                                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                    {t("howto.tips.intShareCaption")}
                                </Typography>
                                <Box sx={toolbarDemoBoxSx}>
                                    <Tooltip title={t("puzzle.playGrid.btn-share")} arrow>
                                        <Box component="span" sx={playToolbarIconDemoSx} aria-hidden>
                                            <ShareIcon />
                                        </Box>
                                    </Tooltip>
                                </Box>
                            </li>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 3 }} />

                    <Box sx={{ mb: 4 }}>
                        <TipsSectionHeading icon={EmojiEventsOutlinedIcon}>
                            {t("howto.tips.sectionAdvanced")}
                        </TipsSectionHeading>
                        <Box component="ul" sx={{ pl: 3, m: 0, "& li": { mb: 1.5, fontSize: "1.1rem", lineHeight: 1.8 } }}>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.advUltimateLabel")}
                                    body={t("howto.tips.advUltimateBody")}
                                />
                            </li>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.advLaserLabel")}
                                    body={t("howto.tips.advLaserBody")}
                                />
                            </li>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.advAnteLabel")}
                                    body={t("howto.tips.advAnteBody")}
                                />
                            </li>
                            <li>
                                <LabeledParagraph
                                    dense
                                    label={t("howto.tips.advHungryLabel")}
                                    body={t("howto.tips.advHungryBody")}
                                />
                            </li>
                        </Box>
                    </Box>
                </Item>
            </Box>
        </Box>
    );
}
